// ══════════════════════════════════════════════════════════════════════════════
// ErrorConfigPage.tsx — Configurazione notifiche errori (solo SUPERADMIN)
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { API_DOCS } from './ErrorConfigPage.api';
import BACKEND_URL from '../../config.js';

const API_URL = BACKEND_URL;

// Helper per recuperare il token JWT da localStorage
function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

interface SmtpConfig {
  id: number;
  tipo: string;
  host: string;
  fromEmail: string;
  fromName: string;
  attiva: boolean;
  societaNome: string | null;
}

interface ErrorLogEntry {
  id: number;
  tipo: string;
  message: string;
  stack?: string;
  url?: string;
  userId?: number;
  societaId?: number;
  userAgent?: string;
  emailSent: boolean;
  createdAt: string;
}

const ErrorConfigPage: React.FC = () => {
  // ── State ───────────────────────────────────────────────────────────────────
  const [smtpConfigs, setSmtpConfigs] = useState<SmtpConfig[]>([]);
  const [selectedSmtpId, setSelectedSmtpId] = useState<number | null>(null);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [throttleMinutes, setThrottleMinutes] = useState<number>(10);
  const [newEmail, setNewEmail] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [errors, setErrors] = useState<ErrorLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSmtp, setLoadingSmtp] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedError, setSelectedError] = useState<ErrorLogEntry | null>(null);

  // ── Load SMTP configs, config and errors ────────────────────────────────────
  useEffect(() => {
    loadSmtpConfigs();
    loadConfig();
    loadErrors();
  }, []);

  const loadSmtpConfigs = async () => {
    try {
      setLoadingSmtp(true);
      // @api GET /api/errors/smtp-options -> {"options":"SmtpConfig[]"}
      const res = await fetch(`${API_URL}/api/errors/smtp-options`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      console.log('[ErrorConfigPage] SMTP options response status:', res.status);

      if (res.ok) {
        const data = await res.json();
        console.log('[ErrorConfigPage] SMTP options data:', data);
        setSmtpConfigs(data.options || []);
      } else {
        const errorText = await res.text();
        console.error('[ErrorConfigPage] SMTP options error:', res.status, errorText);
      }
    } catch (err) {
      console.error('Errore caricamento configurazioni SMTP:', err);
    } finally {
      setLoadingSmtp(false);
    }
  };

  const loadConfig = async () => {
    try {
      // @api GET /api/errors/config -> {"emailTo":"string","smtpConfigId":"number","throttleMinutes":"number"}
      const res = await fetch(`${API_URL}/api/errors/config`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        // Backend restituisce emailTo come stringa comma-separated
        const emailTo = data.emailTo || '';
        setRecipients(emailTo ? emailTo.split(',').map((e: string) => e.trim()) : []);

        if (data.smtpConfigId) {
          setSelectedSmtpId(data.smtpConfigId);
        }

        if (data.throttleMinutes) {
          setThrottleMinutes(data.throttleMinutes);
        }
      }
    } catch (err) {
      console.error('Errore caricamento configurazione:', err);
    }
  };

  const loadErrors = async () => {
    try {
      setLoading(true);
      // @api GET /api/errors/recent -> {"errors":"ErrorLog[]"}
      const res = await fetch(`${API_URL}/api/errors/recent`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setErrors(data.errors || []);
      }
    } catch (err) {
      console.error('Errore caricamento errori:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Handlers: Recipients ────────────────────────────────────────────────────
  const handleAddEmail = () => {
    const trimmed = newEmail.trim();
    if (!trimmed) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      alert('Email non valida');
      return;
    }

    if (recipients.includes(trimmed)) {
      alert('Email già presente');
      return;
    }

    setRecipients([...recipients, trimmed]);
    setNewEmail('');
  };

  const handleRemoveEmail = (email: string) => {
    setRecipients(recipients.filter(r => r !== email));
  };

  const handleSaveConfig = async () => {
    if (!selectedSmtpId) {
      alert('Seleziona un client SMTP prima di salvare');
      return;
    }

    if (recipients.length === 0) {
      alert('Aggiungi almeno un destinatario email');
      return;
    }

    try {
      setSaving(true);
      // Backend si aspetta emailTo come stringa comma-separated
      // @api POST /api/errors/config {"emailTo":"string","smtpConfigId":"number","throttleMinutes":"number"} -> {"ok":"true"}
      const res = await fetch(`${API_URL}/api/errors/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          emailTo: recipients.join(','),
          smtpConfigId: selectedSmtpId,
          throttleMinutes
        })
      });

      if (res.ok) {
        alert('Configurazione salvata con successo');
      } else {
        const errorData = await res.json().catch(() => ({}));
        alert(`Errore durante il salvataggio: ${errorData.error || 'errore sconosciuto'}`);
      }
    } catch (err) {
      console.error('Errore salvataggio configurazione:', err);
      alert('Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ── Handlers: Test Email ────────────────────────────────────────────────────
  const handleSendTestEmail = async () => {
    const trimmed = testEmail.trim();
    if (!trimmed) {
      alert('Inserisci un indirizzo email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      alert('Email non valida');
      return;
    }

    try {
      setTesting(true);
      // @api POST /api/errors/test-email {"email":"string"} -> {"ok":"true"}
      const res = await fetch(`${API_URL}/api/errors/test-email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ email: trimmed })
      });

      if (res.ok) {
        alert(`Email di test inviata con successo a ${trimmed}`);
      } else {
        const data = await res.json();
        alert(`Errore: ${data.error || 'Invio fallito'}`);
      }
    } catch (err) {
      console.error('Errore invio email di test:', err);
      alert('Errore durante l\'invio dell\'email di test');
    } finally {
      setTesting(false);
    }
  };

  // ── Handlers: Error Detail ──────────────────────────────────────────────────
  const handleViewError = (error: ErrorLogEntry) => {
    setSelectedError(error);
  };

  const handleCloseDetail = () => {
    setSelectedError(null);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="bop-page-header">
        <h1 className="bop-page-title">Configurazione Errori</h1>
      </div>

      {/* Sezione 1: Configurazione Destinatari */}
      <div className="bop-card">
        <h2 className="bop-card-title">Destinatari Email Notifiche</h2>
        <p className="bop-card-subtitle">
          Gli errori applicativi verranno notificati via email ai seguenti destinatari
        </p>

        {/* Dropdown Client SMTP */}
        <div className="bop-form-group">
          <label className="bop-label">Client SMTP</label>
          {loadingSmtp ? (
            <p className="bop-text-muted">Caricamento configurazioni SMTP...</p>
          ) : smtpConfigs.length === 0 ? (
            <p className="bop-text-danger">
              Nessun client SMTP configurato — vai a <a href="/impostazioni/smtp-email">Impostazioni &gt; SMTP</a>
            </p>
          ) : (
            <select
              className="bop-select"
              value={selectedSmtpId || ''}
              onChange={e => setSelectedSmtpId(Number(e.target.value))}
            >
              <option value="">Seleziona un client SMTP</option>
              {smtpConfigs.map(config => (
                <option key={config.id} value={config.id}>
                  [{config.tipo}] — {config.societaNome || 'Tutte le società'} — {config.host} ({config.fromEmail})
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Email Destinatari</label>
          {recipients.length === 0 ? (
            <p className="bop-text-muted">Nessun destinatario configurato</p>
          ) : (
            <div className="bop-recipient-list">
              {recipients.map(email => (
                <div key={email} className="bop-recipient-item">
                  <span>{email}</span>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="bop-btn-icon"
                    title="Rimuovi"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Aggiungi destinatario</label>
          <div className="bop-input-with-button">
            <input
              type="email"
              className="bop-input"
              placeholder="email@example.com"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddEmail()}
            />
            <button onClick={handleAddEmail} className="bop-btn-secondary">
              Aggiungi
            </button>
          </div>
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Throttling (minuti tra email duplicate)</label>
          <input
            type="number"
            className="bop-input"
            min="1"
            max="1440"
            value={throttleMinutes}
            onChange={e => setThrottleMinutes(Number(e.target.value))}
          />
          <p className="bop-text-muted">
            Le email duplicate dello stesso errore verranno inviate solo dopo questo intervallo di tempo (default: 10 minuti)
          </p>
        </div>

        <button
          onClick={handleSaveConfig}
          disabled={saving || !selectedSmtpId}
          className="bop-btn-primary"
        >
          {saving ? 'Salvataggio...' : 'Salva Configurazione'}
        </button>
      </div>

      {/* Sezione 2: Test Email */}
      <div className="bop-card">
        <h2 className="bop-card-title">Invia Email di Test</h2>
        <p className="bop-card-subtitle">
          Verifica che il sistema SMTP sia configurato correttamente inviando un'email di test
        </p>

        <div className="bop-form-group">
          <label className="bop-label">Email destinatario test</label>
          <div className="bop-input-with-button">
            <input
              type="email"
              className="bop-input"
              placeholder="test@example.com"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSendTestEmail()}
            />
            <button
              onClick={handleSendTestEmail}
              disabled={testing}
              className="bop-btn-primary"
            >
              {testing ? 'Invio...' : 'Invia Test'}
            </button>
          </div>
        </div>
      </div>

      {/* Sezione 3: Log Errori Recenti */}
      <div className="bop-card">
        <div className="bop-card-header-with-action">
          <div>
            <h2 className="bop-card-title">Log Errori Recenti</h2>
            <p className="bop-card-subtitle">Ultimi 50 errori registrati dal sistema</p>
          </div>
          <button onClick={loadErrors} disabled={loading} className="bop-btn-secondary">
            {loading ? 'Caricamento...' : 'Aggiorna'}
          </button>
        </div>

        {loading ? (
          <p className="bop-text-muted">Caricamento errori...</p>
        ) : errors.length === 0 ? (
          <p className="bop-text-muted">Nessun errore registrato</p>
        ) : (
          <div className="bop-error-log-list">
            {errors.map(error => (
              <div key={error.id} className="bop-error-log-item">
                <div className="bop-error-log-header">
                  <div>
                    <span className="bop-error-log-tipo">{error.tipo}</span>
                    <span className="bop-error-log-date">
                      {new Date(error.createdAt).toLocaleString('it-IT')}
                    </span>
                  </div>
                  {error.emailSent && <span className="bop-badge-info">Email inviata</span>}
                </div>
                <p className="bop-error-log-message">{error.message}</p>
                {error.url && (
                  <p className="bop-error-log-url">
                    <strong>URL:</strong> {error.url}
                  </p>
                )}
                <button
                  onClick={() => handleViewError(error)}
                  className="bop-btn-secondary bop-btn-sm"
                >
                  Dettagli
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Dettaglio Errore */}
      {selectedError && (
        <div className="bop-modal-overlay" onClick={handleCloseDetail}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Dettaglio Errore #{selectedError.id}</h2>
              <button onClick={handleCloseDetail} className="modal-close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="bop-error-detail-section">
                <label className="bop-label">Tipo</label>
                <p>{selectedError.tipo}</p>
              </div>
              <div className="bop-error-detail-section">
                <label className="bop-label">Messaggio</label>
                <p>{selectedError.message}</p>
              </div>
              {selectedError.stack && (
                <div className="bop-error-detail-section">
                  <label className="bop-label">Stack Trace</label>
                  <pre className="bop-error-stack">{selectedError.stack}</pre>
                </div>
              )}
              {selectedError.url && (
                <div className="bop-error-detail-section">
                  <label className="bop-label">URL</label>
                  <p>{selectedError.url}</p>
                </div>
              )}
              {selectedError.userId && (
                <div className="bop-error-detail-section">
                  <label className="bop-label">User ID</label>
                  <p>{selectedError.userId}</p>
                </div>
              )}
              {selectedError.societaId && (
                <div className="bop-error-detail-section">
                  <label className="bop-label">Società ID</label>
                  <p>{selectedError.societaId}</p>
                </div>
              )}
              {selectedError.userAgent && (
                <div className="bop-error-detail-section">
                  <label className="bop-label">User Agent</label>
                  <p className="bop-error-user-agent">{selectedError.userAgent}</p>
                </div>
              )}
              <div className="bop-error-detail-section">
                <label className="bop-label">Data/Ora</label>
                <p>{new Date(selectedError.createdAt).toLocaleString('it-IT')}</p>
              </div>
              <div className="bop-error-detail-section">
                <label className="bop-label">Email Inviata</label>
                <p>{selectedError.emailSent ? 'Sì' : 'No'}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleCloseDetail} className="bop-btn-secondary">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* API Inspector — visible solo a SUPERADMIN */}
      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
};

export default ErrorConfigPage;
